<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { ServerForm } from '~/types/servers'
import type { StaffRole } from '~/types/staffPermissions'
import { DEFAULT_PERMISSIONS_BY_ROLE } from '~/types/staffPermissions'

const { t } = useI18n()
const dialogRef = inject('dialogRef') as { value?: { close?: () => void } } | undefined
const serversStore = useServersStore()

const formModel = ref({
  name: '',
  pin_code: '',
  pin_confirm: '',
  employee_number: '',
  role: 'staff' as StaffRole,
  active: true,
})

const permissions = ref(DEFAULT_PERMISSIONS_BY_ROLE('staff'))

watch(() => formModel.value.role, (newRole) => {
  permissions.value = DEFAULT_PERMISSIONS_BY_ROLE(newRole)
})

async function handleSubmit(): Promise<void> {
  const payload: ServerForm = {
    name: formModel.value.name,
    pin_code: formModel.value.pin_code,
    employee_number: formModel.value.employee_number,
    role: formModel.value.role,
    active: formModel.value.active,
    permissions: permissions.value,
  }

  const success = await serversStore.createServer(payload)
  if (success) {
    dialogRef?.value?.close?.()
  }
}
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    id="server-create-form"
    v-model="formModel"
    class="flex"
    type="form"
    :actions="false"
    @submit="handleSubmit"
  >
    <div class="flex gap-3 justify-center">
      <div class="w-1/2">
        <FormKit
          type="text"
          :label="t('Name')"
          :placeholder="t('Maria Garcia')"
          validation="required"
          validation-visibility="live"
          name="name"
        />
        <FormKit
          type="select"
          :label="t('Role')"
          :options="[
            { label: t('Staff'), value: 'staff' },
            { label: t('Manager'), value: 'manager' },
            { label: t('Admin'), value: 'admin' },
          ]"
          validation="required"
          name="role"
        />
        <FormKit
          type="text"
          :label="t('PIN Code')"
          :placeholder="t('4-6 digit PIN')"
          validation="required|number|length:4,6"
          :validation-messages="{
            number: t('PIN must contain only digits'),
            length: t('PIN must be 4-6 digits'),
          }"
          validation-visibility="live"
          name="pin_code"
          inputmode="numeric"
        />
        <FormKit
          type="text"
          :label="t('Confirm PIN')"
          :placeholder="t('4-6 digit PIN')"
          validation="required|confirm:pin_code"
          validation-visibility="live"
          name="pin_confirm"
          inputmode="numeric"
        />
        <FormKit
          type="text"
          :label="t('Employee Number')"
          :placeholder="t('Optional')"
          name="employee_number"
        />
        <FormKit
          type="checkbox"
          :label="t('Active')"
          name="active"
          :value="true"
        />

        <h3 class="text-sm font-semibold text-gray-700 mt-4 mb-3">
          {{ t('Permissions') }}
        </h3>
        <AdminServersStaffPermissionsForm v-model="permissions" />

        <FormKit
          type="submit"
          :label="t('Add server')"
        />
      </div>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
